/**
 * HOH Vibes Studio App landing page — served at /app.
 * - Admin-editable promotional carousel (top)
 * - Static content: hero copy, store buttons, Basic Licence, 6-step guide,
 *   features grid, QR code + smart link
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PromoCarousel from "@/components/app-landing/PromoCarousel";
import { Loader2, Music, Piano, Users, Search, Languages, Church, BookOpen, FileMusic, Check, Sparkles, ArrowRight } from "lucide-react";

const APPLE_URL = "https://apps.apple.com/app/hoh-vibes-studio-hymns-songs/id6794413511";
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.emergent.hymnplayer.fjp0vq";
const SMART_LINK = "https://link-my.app/hohvibes-studio";
const QR_IMAGE = "https://customer-assets-jai6qajn.emergentagent.net/job_vibes-digital-hub/artifacts/io42o6lv_image.png";

const FEATURES = [
  { icon: BookOpen, label: "Lyrics", tone: "text-emerald-300" },
  { icon: FileMusic, label: "Tonic Sol-fa", tone: "text-[#D4AF37]" },
  { icon: Users, label: "SATB", tone: "text-pink-300" },
  { icon: Piano, label: "MIDI", tone: "text-purple-300" },
  { icon: Music, label: "MP3", tone: "text-red-300" },
  { icon: Languages, label: "Multiple Languages", tone: "text-blue-300" },
  { icon: Church, label: "Multiple Denominations", tone: "text-amber-300" },
  { icon: Search, label: "Powerful Search", tone: "text-teal-300" },
];

const STEPS = [
  { n: 1, title: "Search for the App", body: "Search HOH Vibes Studio Hymns & Songs on the App Store or Google Play." },
  { n: 2, title: "Download & Explore for Free", body: "Open the app and select Explore as guest." },
  { n: 3, title: "Choose a Music Feature", body: "Try Audio/MIDI, Tonic Sol-fa or SATB and tap View Basic licence when the feature is locked." },
  { n: 4, title: "Get the Basic Licence", body: "Select HOH Studio — Basic and tap Get Basic." },
  { n: 5, title: "Sign In & Pay", body: "Sign in using the available Google/Apple sign-in option and complete the one-time purchase through the App Store or Google Play." },
  { n: 6, title: "You’re In!", body: "When the account shows You own this, the Basic licence is active." },
];

export default function AppLanding() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/app-promo-slides");
        setSlides(data);
      } catch { /* fall back to empty state inside carousel */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="app-landing" className="bg-black text-white overflow-x-hidden">
      {/* Sunburst / glow behind hero */}
      <div className="relative">
        <div aria-hidden className="absolute inset-x-0 -top-32 h-[540px] pointer-events-none" style={{
          background: "radial-gradient(ellipse 900px 420px at 50% 0%, rgba(212,175,55,0.14), transparent 60%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-16 pb-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">
              <Sparkles size={11} /> HOH Vibes Studio · Hymns &amp; Songs
            </div>
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.02] mt-5">
              Hymns & Songs.
              <span className="block italic text-[#D4AF37]">Reimagined.</span>
            </h1>
            <p className="text-neutral-300 mt-4 text-sm sm:text-base max-w-2xl mx-auto">
              One App · Many Hymnals · Endless Worship. Available worldwide on iPhone, iPad and Android.
            </p>
          </div>

          {/* CAROUSEL */}
          {loading ? (
            <div className="w-full aspect-[3/4] max-h-[80vh] rounded-2xl bg-neutral-900/40 border border-white/5 grid place-items-center">
              <Loader2 className="animate-spin text-[#D4AF37]" size={20} />
            </div>
          ) : (
            <PromoCarousel slides={slides} />
          )}

          {/* Store buttons */}
          <div className="mt-10 flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
            <StoreButton href={APPLE_URL} testid="cta-apple" primary>
              <AppleGlyph />
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] tracking-widest uppercase text-neutral-300">Download on the</span>
                <span className="text-lg font-semibold">App Store</span>
              </span>
            </StoreButton>
            <StoreButton href={PLAY_URL} testid="cta-play">
              <PlayGlyph />
              <span className="flex flex-col items-start leading-tight">
                <span className="text-[10px] tracking-widest uppercase text-neutral-300">Get it on</span>
                <span className="text-lg font-semibold">Google Play</span>
              </span>
            </StoreButton>
          </div>
        </div>
      </div>

      {/* PRICING SECTION */}
      <section id="pricing" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <SectionEyebrow>Basic Licence</SectionEyebrow>
        <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] mt-3 text-center">
          <span className="text-emerald-400">FREE</span> to download.
          <span className="block">Unlock the music for <span className="text-[#D4AF37]">£5 once</span>.</span>
        </h2>
        <p className="text-neutral-400 text-sm text-center max-w-2xl mx-auto mt-4">
          Users can explore the app and access free content including lyrics.
          Upgrade to <b className="text-white">HOH Studio — Basic</b> to unlock premium music features.
        </p>

        <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          <PriceCard
            title="Explore for Free"
            price="£0"
            unit="forever"
            testid="price-free"
            features={[
              "Guest account — no sign-in required",
              "Full lyrics library",
              "Multi-language display",
              "Browse hymnals & collections",
              "Powerful search",
            ]}
          />
          <PriceCard
            title="HOH Studio — Basic"
            price="£5"
            unit="one-time"
            highlight
            badge="Lifetime access"
            testid="price-basic"
            features={[
              "Everything in Free, plus:",
              "Tonic Sol-fa",
              "SATB harmony",
              "MIDI playback",
              "MP3 playback",
              "All available music features",
            ]}
            footer={(
              <div className="text-[10px] tracking-[0.22em] uppercase text-center text-[#D4AF37] font-semibold">
                One payment · Lifetime access · No monthly fees · No subscription
              </div>
            )}
          />
        </div>

        <p className="text-[11px] text-neutral-500 text-center mt-6 max-w-2xl mx-auto">
          £5 or the equivalent price displayed in your local App Store / Google Play storefront.
          This is <b className="text-neutral-300">NOT £5 per month</b>. Purchases on iOS are handled by Apple; on Android by Google Play.
        </p>
      </section>

      {/* 6 STEPS SECTION */}
      <section id="steps" className="relative">
        <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
          background: "linear-gradient(180deg, transparent 0%, rgba(107,20,44,0.25) 25%, rgba(107,20,44,0.25) 75%, transparent 100%)",
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="text-center">
            <SectionEyebrow>How to Get Your Basic Licence</SectionEyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] mt-3">
              <span className="text-[#D4AF37]">6</span> Simple Steps
            </h2>
            <p className="text-neutral-300 text-sm mt-3 max-w-xl mx-auto">
              From download to lifetime access, in under 2 minutes.
            </p>
          </div>

          <ol className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {STEPS.map((s) => (
              <li
                key={s.n}
                data-testid={`step-${s.n}`}
                className="relative rounded-xl border border-[#D4AF37]/20 bg-black/60 backdrop-blur px-5 py-6 hover:border-[#D4AF37]/50 hover:bg-black/70 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-full grid place-items-center bg-gradient-to-br from-[#6B142C] to-[#8B1D3B] border border-[#D4AF37]/40 text-white font-serif-display text-xl">
                    {s.n}
                  </div>
                  <div>
                    <div className="text-white font-semibold tracking-tight">{s.title}</div>
                    <p className="text-neutral-300 text-sm mt-2 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="text-center">
          <SectionEyebrow>What you get with Basic</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl leading-[1.05] mt-3">
            Everything to <span className="italic text-[#D4AF37]">sing</span>, <span className="italic text-[#D4AF37]">rehearse</span> and <span className="italic text-[#D4AF37]">worship</span>
          </h2>
        </div>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.label}
                data-testid={`feature-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="group rounded-xl border border-white/10 bg-white/[0.02] p-5 hover:border-[#D4AF37]/40 hover:bg-white/[0.04] transition-colors"
              >
                <div className={`w-11 h-11 rounded-lg grid place-items-center bg-white/5 border border-white/10 ${f.tone} group-hover:scale-105 transition-transform`}>
                  <Icon size={18} />
                </div>
                <div className="mt-3 text-white text-sm font-semibold">{f.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* QR + Smart Link */}
      <section id="scan" className="relative">
        <div aria-hidden className="absolute inset-x-0 top-0 h-40 pointer-events-none" style={{
          background: "radial-gradient(ellipse 700px 240px at 50% 0%, rgba(212,175,55,0.14), transparent 60%)",
        }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <SectionEyebrow>Scan · Share · Install</SectionEyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-3">One link. Any device.</h2>
          <p className="text-neutral-300 text-sm mt-3 max-w-lg mx-auto">
            Point any phone camera at the QR code — it routes automatically to the App Store or Google Play.
          </p>

          <div className="mt-10 grid sm:grid-cols-2 gap-6 items-center max-w-3xl mx-auto">
            <div className="rounded-2xl bg-white p-6 mx-auto w-fit shadow-[0_20px_80px_-20px_rgba(0,0,0,0.6)]" data-testid="qr-card">
              <img src={QR_IMAGE} alt="HOH Vibes Studio smart link QR code" width={220} height={220} className="block" />
            </div>
            <div className="text-left">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">Smart link</div>
              <a
                data-testid="smart-link"
                href={SMART_LINK}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-2 block text-white text-base sm:text-lg font-mono break-all hover:text-[#D4AF37] transition-colors"
              >
                https://link-my.app/hohvibes-studio
              </a>
              <a
                href={SMART_LINK}
                target="_blank"
                rel="noreferrer noopener"
                className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#e6c04a] text-black px-5 py-3 rounded-full text-[11px] font-semibold tracking-[0.22em] uppercase shadow-lg transition-colors"
              >
                Open smart link <ArrowRight size={14} />
              </a>
              <div className="mt-6 text-[11px] text-neutral-500">Works on iPhone, iPad, Android — routes to the correct store automatically.</div>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-white/10">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-400 hover:text-[#D4AF37] transition-colors"
            >
              Back to hohvibes.com
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- helpers ---------- */
function SectionEyebrow({ children }) {
  return <div className="text-[10px] tracking-[0.32em] uppercase text-[#D4AF37] text-center">{children}</div>;
}

function StoreButton({ href, children, primary, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={testid}
      className={`inline-flex items-center gap-3 px-5 py-3 rounded-xl min-w-[195px] shadow-lg transition-colors ${
        primary
          ? "bg-black border border-white/25 hover:border-[#D4AF37] text-white"
          : "bg-black border border-white/25 hover:border-[#D4AF37] text-white"
      }`}
    >
      {children}
    </a>
  );
}

function PriceCard({ title, price, unit, features, highlight, badge, footer, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-6 sm:p-8 flex flex-col ${
        highlight
          ? "bg-gradient-to-br from-[#1a1206] via-black to-black border border-[#D4AF37]/50 shadow-[0_20px_60px_-20px_rgba(212,175,55,0.35)]"
          : "bg-white/[0.02] border border-white/10"
      }`}
    >
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black px-3 py-1 rounded-full text-[10px] tracking-[0.24em] uppercase font-bold">
          {badge}
        </div>
      )}
      <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-400">{title}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className={`font-serif-display text-5xl sm:text-6xl leading-none ${highlight ? "text-[#D4AF37]" : "text-white"}`}>{price}</div>
        <div className="text-xs text-neutral-500 tracking-widest uppercase">{unit}</div>
      </div>
      <ul className="mt-6 space-y-2.5 text-sm text-neutral-300 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={16} className={`shrink-0 mt-0.5 ${highlight ? "text-[#D4AF37]" : "text-emerald-400"}`} />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      {footer && <div className="mt-6 pt-5 border-t border-white/10">{footer}</div>}
    </div>
  );
}

function AppleGlyph() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 20.28c-.98.96-2.05.83-3.08.36-1.09-.49-2.08-.51-3.23 0-1.44.64-2.2.46-3.06-.36C2.6 15.38 3.68 7.37 9.61 7c1.36.07 2.31.76 3.11.81 1.19-.15 2.3-.86 3.55-.79 1.51.11 2.65.72 3.4 1.82-3.09 1.87-2.36 5.98.54 7.06-.54 1.58-1.31 3.12-2.16 4.38zM12.03 6.73c-.15-2.19 1.61-3.99 3.61-4.14.27 2.43-2.18 4.27-3.61 4.14z" />
    </svg>
  );
}

function PlayGlyph() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#FF3B30" d="M3.5 2.9c-.31.32-.5.82-.5 1.47v15.26c0 .65.19 1.15.51 1.47l.08.08L12 12.09v-.18L3.58 3.5l-.08-.6z" />
      <path fill="#FFCE00" d="M15.55 15.74l-2.6-2.6v-.28l2.6-2.6.06.03 3.08 1.75c.88.5.88 1.32 0 1.82l-3.08 1.75-.06.13z" />
      <path fill="#00A651" d="M15.61 15.71L12.95 13 3.5 22.1c.29.31.77.35 1.31.04l10.8-6.43" />
      <path fill="#00B0FF" d="M15.61 8.29L4.81 2.86c-.54-.31-1.02-.27-1.31.04L12.95 12z" />
    </svg>
  );
}
